import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { track } from '@/analytics/tracker';
import { useFocusTrap } from '@/app/layers/useFocusTrap';
import { useLayer } from '@/app/layers/useLayer';
import { useT } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import type { OnboardingStep } from './steps';
import styles from './Tour.module.css';
import { needsScroll, placeCoach, type CoachPlacement } from './tourRules';

interface Props {
  steps: OnboardingStep[];
  /** Перевести приложение на маршрут шага; true — адрес изменился (замер позже). */
  onNavigate: (step: OnboardingStep) => boolean;
  onFinish: (reason: 'completed' | 'skipped', stepIndex: number) => void;
}

const NAV_DELAY_MS = 420;
const SAME_DELAY_MS = 60;
const TARGET_POLL_MS = 120;
const TARGET_POLL_LIMIT = 16; // ≈2 с ожидания цели (карточки каталога грузятся асинхронно)

/** Первый видимый элемент с `data-tour="key"` — в шапке и нижней навигации якоря дублируются. */
function findTarget(key: string): HTMLElement | null {
  const all = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${key}"]`));
  return all.find((el) => el.offsetParent !== null && el.getBoundingClientRect().width > 0) ?? null;
}

/** Коуч-марки (README §8): прожектор вокруг цели, карточка 360px, переходы .45s, клавиши ←/→/Enter/Esc. */
export function TourOverlay({ steps, onNavigate, onFinish }: Props) {
  const t = useT();
  const [index, setIndex] = useState(0);
  const [placement, setPlacement] = useState<CoachPlacement | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const indexRef = useRef(0);
  indexRef.current = index;
  const total = steps.length;
  const step = steps[index]!;

  const skip = useCallback(() => onFinish('skipped', indexRef.current), [onFinish]);
  useLayer(true, skip, { lockScroll: false, history: false });
  useFocusTrap(boxRef, true);

  const measure = useCallback((key: string, polls = 0, scrolls = 0) => {
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const boxHeight = boxRef.current?.offsetHeight || 200;
    const el = findTarget(key);
    if (!el) {
      if (polls < TARGET_POLL_LIMIT) {
        timer.current = setTimeout(() => measure(key, polls + 1, scrolls), TARGET_POLL_MS);
        setPlacement((prev) => prev ?? placeCoach(null, viewport, boxHeight));
        return;
      }
      setPlacement(placeCoach(null, viewport, boxHeight));
      return;
    }
    const r = el.getBoundingClientRect();
    if (needsScroll(r, viewport.height) && scrolls < 3) {
      const before = r.top;
      el.scrollIntoView({ block: 'center', behavior: 'auto' });
      // Липкая шапка и нижняя навигация не двигаются — принимаем позицию как есть.
      if (Math.abs(el.getBoundingClientRect().top - before) > 1) {
        timer.current = setTimeout(() => measure(key, polls, scrolls + 1), 260);
        return;
      }
    }
    setPlacement(
      placeCoach(
        { left: r.left, top: r.top, width: r.width, height: r.height },
        viewport,
        boxHeight,
      ),
    );
  }, []);

  const apply = useCallback(
    (i: number) => {
      const next = steps[i];
      if (!next) return;
      if (timer.current) clearTimeout(timer.current);
      const navigated = onNavigate(next);
      setIndex(i);
      track('onboarding_step_viewed', { metadata: { step: i + 1, step_key: next.key } });
      timer.current = setTimeout(
        () => {
          measure(next.target);
          setTimeout(() => boxRef.current?.focus(), 30);
        },
        navigated ? NAV_DELAY_MS : SAME_DELAY_MS,
      );
    },
    [steps, onNavigate, measure],
  );

  useEffect(() => {
    apply(0);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Перезамер при ресайзе и прокрутке.
  useEffect(() => {
    let raf = 0;
    const onChange = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measure(steps[indexRef.current]!.target));
    };
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [measure, steps]);

  const go = useCallback(
    (delta: 1 | -1) => {
      const i = indexRef.current;
      const current = steps[i]!;
      if (delta === -1) {
        if (i === 0) return;
        track('onboarding_back', { metadata: { step: i + 1, step_key: current.key } });
        apply(i - 1);
        return;
      }
      track('onboarding_next', { metadata: { step: i + 1, step_key: current.key } });
      if (i + 1 >= total) onFinish('completed', i);
      else apply(i + 1);
    },
    [steps, total, apply, onFinish],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'Enter' && document.activeElement === boxRef.current) go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  const spot = placement?.spot ?? null;
  const tip = placement?.tip ?? { x: -9999, y: -9999 };
  const width = placement?.width ?? Math.min(360, window.innerWidth - 32);

  return createPortal(
    <div
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onb-title"
      data-tour-overlay="true"
    >
      <div className={cx(styles.backdrop, !spot && styles.backdropDim)} />
      {spot ? (
        <>
          <div
            className={styles.spot}
            style={{ left: spot.x, top: spot.y, width: spot.w, height: spot.h }}
          />
          <span
            className={styles.halo}
            style={{ left: spot.x, top: spot.y, width: spot.w, height: spot.h }}
          />
        </>
      ) : null}
      <div
        ref={boxRef}
        tabIndex={-1}
        className={cx(styles.coach, placement && styles.coachReady)}
        style={{ left: tip.x, top: tip.y, width }}
      >
        <div className={styles.head}>
          <span className={styles.eyebrow}>
            {index + 1} / {total} · {t(step.section)}
          </span>
          <div className={styles.dots} aria-hidden="true">
            {steps.map((s, k) => (
              <span
                key={s.key}
                className={cx(
                  styles.dot,
                  k === index && styles.dotCurrent,
                  k <= index && styles.dotDone,
                )}
              />
            ))}
          </div>
        </div>
        <h2 id="onb-title" className={styles.title}>
          {t(step.title)}
        </h2>
        <p className={styles.body}>{t(step.body)}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.skip} onClick={skip}>
            {t('tour.skip')}
          </button>
          <span className={styles.spacer} />
          {index > 0 ? (
            <button type="button" className={styles.back} onClick={() => go(-1)}>
              {t('tour.back')}
            </button>
          ) : null}
          <button type="button" className={styles.next} onClick={() => go(1)}>
            {index === total - 1 ? t('tour.start') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
